import { graphql } from "gatsby"
import React, { useContext } from "react"
import { css } from "styled-components"
import Text from "../atoms/text"
import { IStyleVariant } from "../atoms/variant-container"
import { T } from "../styles/typography"
import { BackgroundColourContext } from "../util/background-colour-context"
import mergeVariants from "../util/merge-variants"
import ContentBlock from "./content-block"
import IconTitle from "./icon-title"

interface IIconTitleCardProps {
  icon: JSX.Element
  title: string
  description: string
  titleTag?: string
  descTag?: string
  titleVariants?: IStyleVariant | any[]
  titleAlignmentVariants?: IStyleVariant | any[]
  descriptionVariants?: IStyleVariant | any[]
  iconVariants?: IStyleVariant | any[]
  className?: string
}

export default function IconTitleCard({
  icon,
  title,
  description,
  titleTag,
  descTag,
  titleVariants,
  titleAlignmentVariants,
  descriptionVariants,
  iconVariants,
  className,
}: IIconTitleCardProps) {
  return (
    <div className={className}>
      <IconTitle
        icon={icon}
        title={title}
        titleTag={titleTag}
        textVariants={mergeVariants(
          [
            css`
              ${T.sm.h4`500`}
              color: var(--slate);
            `,
            T.md.h4`500`,
            T.lg.h5`500`,
          ],
          titleVariants
        )}
        alignmentVariants={titleAlignmentVariants}
        iconVariants={iconVariants}
      />
      <Text.Body
        variants={mergeVariants(
          [
            css`
              margin: 16px 0 0;
            `,
          ],
          descriptionVariants
        )}
        as={descTag}
      >
        {description}
      </Text.Body>
    </div>
  )
}

export interface IContentfulIconTitleCard
  extends Omit<
    IIconTitleCardProps,
    "icon" | "title" | "description" | "descriptionVariants" | "descTag"
  > {
  __typename: "ContentfulContentBlockWithIcon"
  icon: {
    file: {
      url: string
      contentType: string
    }
  }
  title: {
    title: string
  }
  body: {
    raw: string
    references: any[]
  }
  bodyVariants?: IStyleVariant | any[]
}

/** Adapter for ContentfulContentBlockWithIcon to IconTitle w/ RichText body */
export const ContentfulIconTitleCard = ({
  icon,
  title,
  body,
  titleTag,
  titleVariants,
  titleAlignmentVariants,
  iconVariants,
  bodyVariants,
  className,
}: IContentfulIconTitleCard) => {
  const {
    state: { dark },
  } = useContext(BackgroundColourContext)
  return (
    <div className={className}>
      <IconTitle
        icon={<img src={icon?.file?.url} alt="" />}
        title={title ? title.title : ""}
        titleTag={titleTag}
        textVariants={mergeVariants(
          [
            css`
              ${T.sm.h4`500`}
              color: ${dark ? `var(--smoke)` : `var(--slate)`};
            `,
            T.md.h4`500`,
            T.lg.h5`500`,
          ],
          titleVariants
        )}
        alignmentVariants={titleAlignmentVariants}
        iconVariants={iconVariants}
      />
      <ContentBlock.RichText
        variants={mergeVariants(
          [
            css`
              margin: 16px 0 0;
            `,
          ],
          bodyVariants
        )}
      >
        {body}
      </ContentBlock.RichText>
    </div>
  )
}

export const IconTitleCardFragment = graphql`
  fragment ContentfulContentBlockWithIcon on ContentfulContentBlockWithIcon {
    contentful_id
    icon {
      file {
        url
      }
    }
    title {
      title
    }
    body {
      raw
    }
  }
`
export const ICON_TITLE_CARD_TYPENAME = "ContentfulContentBlockWithIcon"
